import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Edit,
  Delete,
  LocalOffer,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import PromoCodeFormDialog from '../components/PromoCodeFormDialog';

interface PromoCode {
  _id: string;
  code: string;
  value: number;
  valueCap?: number;
  type: 'percentage' | 'amount';
  isActive: boolean;
  maxUsage?: number;
  usageLeft?: number;
  promoCodeType?: string;
  expiresAt?: string;
  studio?: { _id: string; studioName: string } | string;
  event?: { _id: string; eventName?: string } | string;
  session?: { _id: string } | string;
  createdAt: string;
}

const PromoCodesPage: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getPromoCodes({});
      setPromoCodes(response.promoCodes || response.data || []);
    } catch (err: any) {
      console.error('Error fetching promo codes:', err);
      setError(err.message || 'Failed to fetch promo codes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, promoCode: PromoCode) => {
    setAnchorEl(event.currentTarget);
    setSelectedPromoCode(promoCode);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedPromoCode here - let it be cleared when dialog closes
  };

  const handleEditClick = () => {
    // Close menu first
    setAnchorEl(null);
    // Open edit dialog - selectedPromoCode is already set from handleMenuOpen
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedPromoCode) return;
    try {
      await apiService.deletePromoCode(selectedPromoCode._id);
      await fetchPromoCodes();
      setDeleteDialogOpen(false);
      setSelectedPromoCode(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete promo code');
    }
  };

  const filteredPromoCodes = promoCodes.filter(
    (promoCode) =>
      promoCode.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type: string) => {
    return type === 'percentage' ? colors.warning : colors.success;
  };

  const formatValue = (promoCode: PromoCode) => {
    if (promoCode.type === 'percentage') {
      return `${promoCode.value}%`;
    }
    return `₹${promoCode.value}`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700} color={colors.textPrimary}>
          Promo Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          Create Promo Code
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ border: `1px solid ${colors.divider}` }}>
        <CardContent>
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Search promo codes..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background,
                },
              }}
            />
          </Box>

          {filteredPromoCodes.length === 0 ? (
            <Box textAlign="center" py={4}>
              <LocalOffer sx={{ fontSize: 64, color: colors.textSecondary, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>
                {searchTerm ? 'No promo codes found' : 'No promo codes yet'}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary} mt={1}>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Create your first promo code to get started'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.background }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Studio</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPromoCodes.map((promoCode) => (
                    <TableRow key={promoCode._id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight={600}>
                          {promoCode.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={promoCode.type}
                          size="small"
                          sx={{
                            backgroundColor: `${getTypeColor(promoCode.type)}15`,
                            color: getTypeColor(promoCode.type),
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatValue(promoCode)}
                        </Typography>
                        {promoCode.valueCap && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Cap: ₹{promoCode.valueCap}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={promoCode.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: promoCode.isActive
                              ? `${colors.success}15`
                              : `${colors.textSecondary}15`,
                            color: promoCode.isActive
                              ? colors.success
                              : colors.textSecondary,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Check if maxUsage exists (new promo codes) */}
                        {promoCode.maxUsage != null && promoCode.maxUsage > 0 ? (
                          <Typography variant="body2">
                            {promoCode.maxUsage - (promoCode.usageLeft || 0)} / {promoCode.maxUsage}
                          </Typography>
                        ) : 
                        /* Backward compatibility: Check usageLeft for old promo codes without maxUsage */
                        (promoCode.usageLeft != null && promoCode.usageLeft !== 999999 && promoCode.usageLeft > 0) ? (
                          <Typography variant="body2">
                            {promoCode.usageLeft} remaining
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unlimited
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {promoCode.expiresAt ? (
                          <Typography variant="body2">
                            {new Date(promoCode.expiresAt).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No expiry
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {promoCode.promoCodeType || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {promoCode.studio ? (
                          <Typography variant="body2">
                            {typeof promoCode.studio === 'object' ? promoCode.studio.studioName : promoCode.studio}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {promoCode.event ? (
                          <Typography variant="body2">
                            {typeof promoCode.event === 'object' ? (promoCode.event.eventName || promoCode.event._id) : promoCode.event}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, promoCode)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick();
          }}
        >
          <Edit sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }} 
          sx={{ color: colors.error }}
        >
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      <PromoCodeFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchPromoCodes();
          setCreateDialogOpen(false);
        }}
      />

      {selectedPromoCode && (
        <>
          <PromoCodeFormDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedPromoCode(null);
            }}
            onSuccess={() => {
              fetchPromoCodes();
              setEditDialogOpen(false);
              setSelectedPromoCode(null);
            }}
            promoCode={selectedPromoCode}
          />

          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: deleteDialogOpen ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setDeleteDialogOpen(false)}
          >
            <Card
              sx={{ p: 3, maxWidth: 400, width: '90%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6" fontWeight={600} mb={2}>
                Delete Promo Code
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Are you sure you want to delete the promo code "{selectedPromoCode.code}"? This action cannot be undone.
              </Typography>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PromoCodesPage;

